import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  mdiArchiveOutline,
  mdiChevronRight,
  mdiCogOutline,
  mdiHomeOutline,
  mdiPoll,
  mdiWalletOutline
} from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";

import { ReactComponent as LetterLogo } from "assets/letter-logo.svg";
import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import SideBarNavItem from "components/SideBarNavItem";
import { LogoIcon, LogoIconForLight, LogOutSVGIcon } from "components/SVGIcons";
import { routes } from "constants/routes";
import { useUserFeature } from "hooks/useUserFeature";
import { ISideBarType } from "models/Base";
import { AppDispatch } from "store";
import { logOut } from "store/auth";
import { HAULPAY_ITEMS, LOAD_BOARD_ITEMS, USER_SETTINGS_ITEMS } from "./SidebarItems";
import styles from "./style.module.scss";
import { useColorScheme } from "hooks/useColorScheme";
import eventBus from "../../common/EventBus";

export default function SideBar() {
  const { currentIsDark } = useColorScheme();
  const { isShipper, isDispatcher } = useUserFeature();
  const [isSideBarExpanded, setIsSideBarExpanded] = useState(true);
  const [isLogoutConfirmationModalOpen, toggleLogoutConfirmationModalOpen] = useToggle();
  const dispatch = useDispatch<AppDispatch>();

  const SIDE_BAR_ITEMS_WITH_PERMISSION = [
    {
      link: routes.HOME,
      title: "Home",
      icon: mdiHomeOutline
    },
    {
      title: "Finance",
      icon: mdiWalletOutline,
      children: HAULPAY_ITEMS().filter((item) => !item.hidden),
      hidden: isShipper || isDispatcher
    },
    {
      title: "Load Board",
      icon: mdiArchiveOutline,
      children: LOAD_BOARD_ITEMS().filter((item) => !item.hidden)
    },
    {
      link: routes.RATE_TREND,
      title: "Rate Trend",
      icon: mdiPoll
    },
    {
      title: "Settings",
      link: routes.SETTINGS,
      icon: mdiCogOutline,
      children: USER_SETTINGS_ITEMS().filter((item) => !item.hidden)
    }
  ].filter(Boolean) as ISideBarType[];

  const userLogOut = () => {
    toggleLogoutConfirmationModalOpen();
    dispatch(logOut());
  };

  useEffect(() => {
    const listener = (value: boolean) => {
      setIsSideBarExpanded(!value);
    };
    eventBus.on("hideSidebar", listener);

    return () => {
      eventBus.off("hideSidebar", listener);
    };
  }, []);

  return (
    <>
      <div className={clsx(styles.sidebar, isSideBarExpanded ? "w-70 px-4" : "w-32 pl-0.5 pt-10")}>
        <div className={clsx(styles.logo, !isSideBarExpanded && "pb-2 !ml-4.5")}>
          {currentIsDark ? (
            <LogoIcon className="mb-1 size-7" />
          ) : (
            <LogoIconForLight className="mb-1 size-7" />
          )}
          <LetterLogo className={clsx(styles.textLogo, !isSideBarExpanded && "hidden")} />
        </div>
        <ul className={styles.menuWrapper}>
          {SIDE_BAR_ITEMS_WITH_PERMISSION.filter((item) => !item.hidden).map((item) => (
            <SideBarNavItem
              key={item.title}
              item={item}
              isSideBarExpanded={isSideBarExpanded}
              setIsSideBarExpanded={setIsSideBarExpanded}
            />
          ))}
        </ul>
        <button onClick={toggleLogoutConfirmationModalOpen} className={styles.logoutButton}>
          <LogOutSVGIcon />
          {isSideBarExpanded && <span>Logout</span>}
        </button>
      </div>
      <Button
        iconButton
        rounded
        type="button"
        className={clsx(styles.collapseButton, "!bg-gradient-to-r !to-background !from-popover")}
        onClick={() => setIsSideBarExpanded(!isSideBarExpanded)}>
        <Icon
          path={mdiChevronRight}
          className={clsx(styles.arrowIcon, isSideBarExpanded && "rotate-180")}
        />
      </Button>

      <ConfirmationModal
        isConfirmModalOpen={isLogoutConfirmationModalOpen}
        toggleConfirmModal={toggleLogoutConfirmationModalOpen}
        labelCancelButton="OK"
        labelOKButton="Cancel"
        onCancel={userLogOut}
        onOK={toggleLogoutConfirmationModalOpen}
        content="Are you sure you want to log out?"
      />
    </>
  );
}
